import type { UseQueryResult } from '@tanstack/react-query'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'

// ─── Pure row-matching core ────────────────────────────────────────────────
// These operate on an already-settled ChartRegistryRow[] and carry NO
// query-lifecycle concerns (loading/error). The UseQueryResult wrappers below
// layer loading/error handling on top of them (dashboard tiles hold a
// react-query result); the advisor tools consume the pure functions directly
// (they `await getCharts()` as a plain promise, so they never hold a UseQueryResult).

export type ChartRegistryRowPick =
  | { status: 'missing'; walkFn: string }
  | { status: 'ok'; row: ChartRegistryRow }

// CANVAS_TILE_EXCLUSIONS: summary_bar_strip, metric_driver_bars, and dynamics_panels
// rows share walk functions with canvas tiles (summary_bar_strip shares walk_timeline;
// metric_driver_bars shares walk_ratio_absolute/walk_absolute; dynamics_panels shares
// walk_timeline/walk_ratio) but are resolved separately by their own tile components.
// Without this exclusion an excluded row could sort first and thread the wrong
// default_facet/params into the timeline or absolute_bar tile.
//
// The exclusions are a CANVAS-TILE concern only. Consumers that need just
// (fact_table, walk variant) — the advisor's query_kpi — must pass [] instead:
// on CampaignPerformance EVERY enabled registry row is metric_driver_bars, so
// excluding them there means no KPI resolves at all (found live in the
// 2026-07-03 F2 gate).
export const CANVAS_TILE_EXCLUSIONS: readonly string[] = [
  'summary_bar_strip',
  'metric_driver_bars',
  'dynamics_panels',
]

export function pickChartRegistryRow(
  rows: ChartRegistryRow[],
  walkFn: string,
  excludeChartTypes: readonly string[] = CANVAS_TILE_EXCLUSIONS,
): ChartRegistryRowPick {
  const row = rows.find(
    (r) => r.walk_function === walkFn && !excludeChartTypes.includes(r.chart_type),
  )
  if (!row) return { status: 'missing', walkFn }
  return { status: 'ok', row }
}

// A chart tile of a given kind is backed by EITHER the conventional walk function
// (walk_timeline / walk_absolute) OR — for ratio kpis (Sales RoI/CpO/OpC/Mediashare)
// — the ratio variant (walk_ratio / walk_ratio_absolute). Both produce identical
// row shapes; only the RPC dispatch differs. This resolves which variant the active
// chart_registry row carries so the caller can fire the matching walk hook.
export type WalkVariant = 'standard' | 'ratio'

export type ChartRegistryRowKindPick =
  | { status: 'missing'; walkFn: string }
  | { status: 'ok'; row: ChartRegistryRow; variant: WalkVariant }

// Try the standard walk function first, then the ratio variant. A 'missing' result
// is reported against the standard function name (the primary expectation) so a
// loud-failure message stays meaningful.
export function pickChartRegistryRowForKind(
  rows: ChartRegistryRow[],
  standardFn: string,
  ratioFn: string,
  excludeChartTypes: readonly string[] = CANVAS_TILE_EXCLUSIONS,
): ChartRegistryRowKindPick {
  const standard = pickChartRegistryRow(rows, standardFn, excludeChartTypes)
  if (standard.status === 'ok') return { status: 'ok', row: standard.row, variant: 'standard' }
  const ratio = pickChartRegistryRow(rows, ratioFn, excludeChartTypes)
  if (ratio.status === 'ok') return { status: 'ok', row: ratio.row, variant: 'ratio' }
  return { status: 'missing', walkFn: standardFn }
}

// ─── UseQueryResult wrappers ────────────────────────────────────────────────

export type ChartRegistryLookup =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'missing'; walkFn: string }
  | { status: 'ok'; row: ChartRegistryRow }

// Loud-failure replacement for the silent `charts.data?.find(...) ?? null` +
// `if (!row) return null` recipe (Batch B F1 pattern). Consumers MUST visibly
// handle the missing case via JSX so a chart_registry row gap can't silently
// disappear a tile. Delegates the row match to pickChartRegistryRow.
export function findChartRegistryRow(
  charts: UseQueryResult<ChartRegistryRow[], Error>,
  walkFn: string,
): ChartRegistryLookup {
  if (charts.isLoading) return { status: 'loading' }
  if (charts.error) return { status: 'error', error: charts.error }
  if (!charts.data) return { status: 'loading' }
  return pickChartRegistryRow(charts.data, walkFn)
}

export type ChartRegistryKindLookup =
  | { status: 'loading' }
  | { status: 'error'; error: Error }
  | { status: 'missing'; walkFn: string }
  | { status: 'ok'; row: ChartRegistryRow; variant: WalkVariant }

// Try the standard walk function first, then the ratio variant.
// 'loading'/'error' short-circuit identically to findChartRegistryRow; once the
// query is settled the standard/ratio match is delegated to pickChartRegistryRowForKind.
export function findChartRegistryRowForKind(
  charts: UseQueryResult<ChartRegistryRow[], Error>,
  standardFn: string,
  ratioFn: string,
): ChartRegistryKindLookup {
  if (charts.isLoading) return { status: 'loading' }
  if (charts.error) return { status: 'error', error: charts.error }
  if (!charts.data) return { status: 'loading' }
  return pickChartRegistryRowForKind(charts.data, standardFn, ratioFn)
}
